import React from "react";

import {
  CameraFront as CameraFrontIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import {
  CalendarAppointment,
  STATUS_PALETTE,
  displayStatus,
  formatMinutesRange,
  minutesOfDay,
} from "./geometry";

type Props = {
  appointment: CalendarAppointment;
  now: Date;
  topPx: number;
  heightPx: number;
  leftPct: number;
  widthPct: number;
  dragging: boolean;
  // Pointer-down on the body starts a move (reschedule) gesture; a press with
  // no movement is treated as a click (view) by the parent column.
  onGestureStart: (event: React.PointerEvent) => void;
};

const AppointmentEventBlock: React.FC<Props> = ({
  appointment,
  now,
  topPx,
  heightPx,
  leftPct,
  widthPct,
  dragging,
  onGestureStart,
}) => {
  const { classes, cx } = useStyles();
  const status = displayStatus(appointment, now);
  const palette = STATUS_PALETTE[status];
  const isOnline = appointment.appointmentType === "online";
  const isCancelled = status === "cancelled";
  const compact = heightPx < 44;

  return (
    <div
      className={cx(classes.block, dragging && classes.dragging)}
      style={{
        top: topPx,
        height: Math.max(heightPx, 18),
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        backgroundColor: palette.bg,
        borderColor: palette.border,
        color: palette.text,
      }}
      onPointerDown={onGestureStart}
      role="button"
      tabIndex={0}
    >
      <span
        className={classes.accent}
        style={{ backgroundColor: palette.border }}
      />
      <div className={classes.body}>
        <Typography
          className={cx(classes.name, isCancelled && classes.strike)}
          noWrap
        >
          {isOnline ? (
            <CameraFrontIcon className={classes.typeIcon} />
          ) : (
            <PersonIcon className={classes.typeIcon} />
          )}
          {appointment.clientName ?? "Appointment"}
        </Typography>
        {!compact && (
          <Typography className={classes.time} noWrap>
            {formatMinutesRange(
              minutesOfDay(appointment.start),
              minutesOfDay(appointment.end)
            )}
          </Typography>
        )}
        {!compact && appointment.title && (
          <Typography className={classes.title} noWrap>
            {appointment.title}
          </Typography>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles()(() => ({
  block: {
    position: "absolute",
    display: "flex",
    borderRadius: 6,
    borderStyle: "solid",
    borderWidth: 1,
    overflow: "hidden",
    cursor: "grab",
    touchAction: "none",
    zIndex: 1,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
      zIndex: 3,
    },
  },
  dragging: {
    cursor: "grabbing",
    opacity: 0.85,
    zIndex: 4,
    boxShadow: "0 4px 12px rgba(0,0,0,0.24)",
  },
  accent: {
    width: 3,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
    padding: "2px 6px",
  },
  name: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  strike: {
    textDecoration: "line-through",
  },
  typeIcon: {
    fontSize: 13,
  },
  time: {
    fontSize: 11,
    fontWeight: 600,
    opacity: 0.85,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 11,
    opacity: 0.75,
    lineHeight: 1.2,
  },
}));

export default AppointmentEventBlock;
